import type { ReactNode } from 'react';
import type { BookableAvailability } from './computeBookableSpotsForMap';

/**
 * BLOCKED = reserved; BOOKABLE = user can book; NOT_BOOKABLE = e.g. trainer spot.
 * EMPTY = operator left the slot blank; API still sends row/column. Omitted from {@link RoomLayoutApiResponse.spots} after parse so the grid renders an invisible spacer at that coordinate.
 */
export enum SpotStatus {
  BLOCKED = 'BLOCKED',
  BOOKABLE = 'BOOKABLE',
  NOT_BOOKABLE = 'NOT_BOOKABLE',
  EMPTY = 'EMPTY'
}

export type RoomLayoutSpot = {
  /**
   * Canonical grid key for this spot (equals `getSpotKey(spot)` from this package; derived from API `spot_id` / `id` / `spot_name` / index).
   * Use with `reservedSpotKeys` / `selectedSpotKey` and callbacks. For the booking API body, prefer display label vs id per product rules.
   * When the time-slots API only returns spot_name, spotName and spot_name are the same value.
   */
  spotName: string;
  /**
   * Display label from the API (snake_case). Time-slots API returns this; we also use it as the visible label in the grid.
   * When the API sends both spot_id (e.g. UUID) and spot_name (e.g. "A1"), spotName is the ID and spot_name is the label.
   */
  spot_name: string;
  spot_type_id: string;
  /** Optional: from API when enriched (e.g. time-slots room layout). */
  spot_type_name?: string;
  /** Optional: from API when enriched (e.g. time-slots room layout). */
  spot_type_prefix?: string;
  status: SpotStatus;
  column: number;
  row: number;
};

export type RoomLayoutSpotTypeItem = {
  name: string;
  prefix: string;
};

export type RoomLayoutApiResponse = {
  id: string;
  location_id: string;
  name: string;
  /** Number of display columns (horizontal positions per row). Matches MongoDB room_layouts.columns. */
  columns: number;
  /** Number of display rows (vertical lines). Matches MongoDB room_layouts.rows. */
  rows: number;
  spots: RoomLayoutSpot[];
  total_available_spots: number; // bookable + blocked
  total_bookable_spots: number; // bookable only
  active: boolean;
  spot_types?: RoomLayoutSpotTypeItem[];
  created: string;
  modified: string;
  created_by_user_id: string;
  modified_by_user_id: string;
};

/** Stable codes for UI to map to localized messages (i18n). */
export enum RoomLayoutErrorCode {
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  CLIENT_ERROR = 'CLIENT_ERROR',
  LAYOUT_INACTIVE = 'LAYOUT_INACTIVE',
  UNKNOWN = 'UNKNOWN'
}

export type RoomLayoutError = {
  code: RoomLayoutErrorCode;
  /** Optional: raw message for logging/debugging; do not rely on it for UI copy. */
  message?: string;
};

export type RoomLayoutMapCallbacks = {
  onSuccess?: (data: RoomLayoutApiResponse) => void;
  onError?: (error: RoomLayoutError) => void;
  onSpotSelect?: (spot: RoomLayoutSpot) => void;
  onSkipLoadingAvailable?: (available: boolean) => void;
  onErrorStateChange?: (isError: boolean, error: RoomLayoutError | null) => void;
  onRefetchReady?: (refetch: () => void) => void;
  /** Fired after layout load when bookable count changes (including reserved/assigned overlay updates). */
  onBookableAvailabilityChange?: (info: BookableAvailability) => void;
};

export type RoomLayoutMapProps = {
  envUrl: string;
  layoutId: string;
  userToken: string;
  /**
   * When set, fetches GET /v3.0/time-slots/{timeSlotId}/room-layouts so the layout includes
   * current bookings (spots reserved by others appear as booked). Omit to use GET /v3.0/room-layouts/{layoutId}.
   */
  timeSlotId?: string;
  /**
   * Canonical spot keys reserved for this event (same strings as {@link RoomLayoutSpot.spotName} / `getSpotKey`).
   * Those spots are shown as BLOCKED (booked). Use with refetch after "spot already taken" so the layout updates without needing the time-slot API.
   */
  reservedSpotKeys?: string[];
  /**
   * Selected spot key (same string as {@link RoomLayoutSpot.spotName}).
   */
  selectedSpotKey?: string | null;
  /**
   * Member's current assignment in change-spot flow (light purple filled state).
   * Excluded from {@link reservedSpotKeys} blocking; cleared when {@link selectedSpotKey} differs.
   * Also used for server-assigned locked selection (mid-flow auto-assign / staff assign).
   */
  assignedSpotKey?: string | null;
  /**
   * Optional: hex color for the selected spot (grid + legend "Your Spot").
   * Outline and label use this color; fill uses the same color with transparency.
   * When omitted, default purple styling is used.
   */
  selectedSpotColor?: string;
  /**
   * When true, custom non-bookable spot types show their configured `spot_type_prefix` on grid
   * tiles (BOOKING-3858 / phase 2). When false, those spots use the legacy trainer icon.
   * Defaults to false so consumers opt in (e.g. member app passes FF2).
   */
  useCustomNonBookablePrefixes?: boolean;
  /**
   * When true, the spot status / equipment legend below the grid is not rendered.
   * Host apps pass this from a feature flag (e.g. temporary hide for specific studios).
   */
  hideLegend?: boolean;
  /**
   * When true, spots cannot be selected (preview / read-only, or server-assigned locked
   * selection). Bookable spots stay visually available but taps do not select or call
   * {@link onSpotSelect}. Omits "Selected spot" from the status legend unless
   * {@link assignedSpotKey} is set, since that spot still renders filled.
   */
  selectionDisabled?: boolean;
  renderLoading?: () => ReactNode;
  /**
   * Optional: for dev/test only. Minimum ms to keep loading state before resolving.
   * Use e.g. 12000 to force loading for 12s and verify the 10s Skip option appears.
   */
  loadingMinimumMs?: number;
  /**
   * When true, enables native pinch-to-zoom via SpotGridZoomViewport (Reanimated + gesture-handler).
   * On web/UMD builds pinch is unavailable; use horizontal scroll instead.
   */
  pinchToZoomEnabled?: boolean;
  /**
   * Visible height of the map's scroll slot (e.g. parent ScrollView viewport).
   * When set, caps pinch zoom viewport sizing so tall grids inside a page scroll still pan correctly.
   */
  pinchZoomSlotHeight?: number;
  /**
   * When true with {@link pinchToZoomEnabled}, the map is embedded in a parent vertical ScrollView:
   * the grid uses the full slot height and the legend sits below (page scroll). When false, the
   * legend is reserved inside a fixed flex viewport (no outer page scroll).
   */
  pinchEmbedInPageScroll?: boolean;
} & RoomLayoutMapCallbacks;

export type UseRoomLayoutMapResult = {
  data: RoomLayoutApiResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: RoomLayoutError | null;
  refetch: () => void;
  /** Ref updated synchronously with error state so timer callbacks see it before next render. */
  isErrorRef: { current: boolean };
};
